import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  getAddCreditCardLink,
  getTeacherByUID,
} from "../utils/requests/teachers"
import {
  Accordion,
  AccordionItem,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Input,
  User,
} from "@heroui/react"

import classes from "../styles/TeacherDetails.module.scss"
import { formatReadablePhoneNumber } from "../utils/phoneNumber"
import { getOptionalValue } from "../utils/values"
import DetailsItem from "../components/DetailsItem/DetailsItem"
import CopyIcon from "../components/Icons/CopyIcon"

const TeacherDetails = () => {
  const [loading, setLoading] = useState(false)
  const [teacher, setTeacher] = useState<TeacherDetails>()
  const [user, setUser] = useState<UserDetails>()
  const [addCreditCardLink, setAddCreditCardLink] = useState<string>("")
  const [addCreditCardError, setAddCreditCardError] = useState<string>("")

  const { id } = useParams()
  const navigate = useNavigate()
  const fetchTeacherDetails = async () => {
    setLoading(true)
    const res = await getTeacherByUID(Number(id))
    setTeacher(res.teacher)
    setUser(res.user)
    setLoading(false)
  }

  useEffect(() => {
    fetchTeacherDetails()
  }, [])

  const onAttachInvoicesAccount = () => {
    if (user?.id) {
      navigate(`/teachers/invoices/attach/${user.id}`)
    }
  }
  const onAddCreditCardPress = async () => {
    setAddCreditCardError("")
    const { url, error } = await getAddCreditCardLink(Number(id))

    if (url) {
      return setAddCreditCardLink(url)
    }

    if (error) {
      return setAddCreditCardError(error.message)
    }
    setAddCreditCardError("Unknown Error")
  }

  const copyLink = () => {
    navigator.clipboard.writeText(addCreditCardLink)
  }
  const isActive = ["MONTHLY","GIFT"].includes(user?.plan ?? "NONE")

  return (
    <div className={classes.root}>
      <div className={classes.left}>
        <Card>
          <CardBody className={classes.details}>
            <Accordion
              isCompact
              selectionMode="multiple"
              defaultExpandedKeys={["1", "2", "3"]}
            >
              <AccordionItem key="1" aria-label="פרטים" title="פרטים">
                <div className={classes.row}>
                  <DetailsItem label="שם פרטי" value={teacher?.firstName} />

                  <DetailsItem
                    label="שם משפחה"
                    value={getOptionalValue(teacher?.lastName)}
                  />
                  <DetailsItem
                    label="אימייל"
                    value={getOptionalValue(user?.email)}
                  />
                  <DetailsItem
                    label="מספר נייד"
                    value={
                      user?.phoneNumber
                        ? formatReadablePhoneNumber(user.phoneNumber)
                        : "N/A"
                    }
                  />
                </div>
              </AccordionItem>

              <AccordionItem
                key="2"
                aria-label="מנוי"
                title="מנוי"
              >
                <div className={classes.row}>
                  <DetailsItem
                    label="פעיל"
                    value={isActive}
                  />

                  <DetailsItem
                    label="מחיר מנוי"
                    value={(user?.subscriptionPrice ?? 0).toLocaleString(
                      "he-IL",
                      {
                        style: "currency",
                        currency: "ILS",
                      }
                    )}
                  />

                  <DetailsItem
                    label="תאריך החיוב הבא"
                    value={
                      user?.renewalDate
                        ? new Date(user.renewalDate).toLocaleDateString("he-IL")
                        : "N/A"
                    }
                  />
                  <DetailsItem
                    label="סכום החיוב הבא"
                    value={(user?.nextChargePrice ?? 0).toLocaleString(
                      "he-IL",
                      {
                        style: "currency",
                        currency: "ILS",
                      }
                    )}
                  />
                  <DetailsItem label="חשבונית" value={!!teacher?.invoices} />
                </div>
              </AccordionItem>
              <AccordionItem key="3" aria-label="שיעורים" title="שיעורים">
                <div className={classes.details}>
                  <div className={classes.row}>
                    <DetailsItem
                      label="מחיר שיעור"
                      value={getOptionalValue(
                        teacher?.lessonPrice
                          ? teacher?.lessonPrice.toLocaleString("he-IL", {
                              style: "currency",
                              currency: "ILS",
                            })
                          : "N/A"
                      )}
                    />
                    <DetailsItem
                      label="אורך שיעור (בדקות)"
                      value={getOptionalValue(teacher?.lessonDuration)}
                    />

                    <DetailsItem
                      label="מספר שיעורים מקסימלי בשבוע"
                      value={teacher?.maxLessonsPerWeek}
                    />
                    <DetailsItem
                      label="מספר בקשות כפולות מקסימלי"
                      value={teacher?.maxDuplicatedRequests}
                    />
                  </div>

                  <div className={classes.row}>
                    <DetailsItem
                      label="בקשות מרובות על חלון זמן אחד"
                      value={!!teacher?.allowDoubleBooking}
                    />
                    <DetailsItem
                      label="חסימת חגים יהודיים"
                      value={!!teacher?.blockJewishHolidays}
                    />
                    <DetailsItem
                      label="הזזת אירועים"
                      value={!!teacher?.pushEvents}
                    />
                  </div>
                </div>
              </AccordionItem>
              <AccordionItem
                key="4"
                aria-label="התראות"
                title="התראות"
              >
                <div className={classes.row}>
                  <DetailsItem label="SMS" value={!!teacher?.canGetSMS} />
                  <DetailsItem label="פוש" value={!!teacher?.canGetPush} />
                </div>
              </AccordionItem>
            </Accordion>
          </CardBody>
        </Card>
      </div>
      <div className={classes.right}>
        <Card>
          <CardHeader>פעולות</CardHeader>
          <Divider />
          <CardBody>
            <div className={classes.actions}>
              <Button
                fullWidth
                variant="flat"
                disabled={!user?.id}
                onClick={onAttachInvoicesAccount}
              >
                חיבור לחשבונית דיגיטלית
              </Button>


            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader>מנוי</CardHeader>
          <Divider />
          <CardBody>
            <div className={classes.actions}>

              <Button
                fullWidth
                variant="flat"
                disabled={!user?.id}
                onClick={onAddCreditCardPress}
              >
                הוספת כרטיס אשראי
              </Button>



              {addCreditCardLink && (
                <div className={classes.row}>
                  <Button
                    isIconOnly
                    variant="flat"
                    aria-label="העתקת הקישור"
                    onPress={copyLink}
                    size="lg"
                  >
                    <CopyIcon />
                  </Button>

                  <Input
                    isDisabled
                    type="text"
                    label="קישור"
                    size="sm"
                    defaultValue={addCreditCardLink}
                  />
                </div>
              )}
              {addCreditCardError && (
                <p className="text-small text-red-600	">{addCreditCardError}</p>
              )}

              <Button fullWidth color="danger">
                ביטול המנוי
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default TeacherDetails
